
import React, { useState } from 'react';
import { X, MapPin, Calendar, Users, Clock, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface QuickRideModalProps {
  onClose: () => void;
  onAuthRequired: () => void;
}

export const QuickRideModal: React.FC<QuickRideModalProps> = ({ onClose, onAuthRequired }) => {
  const [step, setStep] = useState<'search' | 'results'>('search');
  const [searchData, setSearchData] = useState({
    from: '',
    to: '',
    date: '',
    time: '',
    passengers: 1
  });

  const handleSearch = () => {
    if (searchData.from && searchData.to && searchData.date) {
      setStep('results');
    }
  };

  const handleBookRide = () => {
    onAuthRequired();
  };

  const sampleRides = [
    {
      id: 1,
      driver: 'Rajesh Kumar',
      rating: 4.8,
      car: 'Honda City',
      time: '09:00 AM',
      price: '₹180',
      duration: '3h 30m',
      seats: 3
    },
    {
      id: 2,
      driver: 'Priya Sharma',
      rating: 4.9,
      car: 'Toyota Innova',
      time: '02:00 PM',
      price: '₹220',
      duration: '3h 15m',
      seats: 2
    },
    {
      id: 3,
      driver: 'Amit Patel',
      rating: 4.7,
      car: 'Maruti Swift',
      time: '06:30 PM',
      price: '₹160',
      duration: '3h 45m',
      seats: 4
    }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900">
            {step === 'search' ? 'Find Your Perfect Ride' : 'Available Rides'}
          </h2>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {step === 'search' && (
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* From Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  From
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Enter departure city"
                    value={searchData.from}
                    onChange={(e) => setSearchData({...searchData, from: e.target.value})}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* To Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  To
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Enter destination city"
                    value={searchData.to}
                    onChange={(e) => setSearchData({...searchData, to: e.target.value})}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Travel Date
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="date"
                    value={searchData.date}
                    onChange={(e) => setSearchData({...searchData, date: e.target.value})}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Time
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="time"
                    value={searchData.time}
                    onChange={(e) => setSearchData({...searchData, time: e.target.value})}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Passengers */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Number of Passengers
                </label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    value={searchData.passengers}
                    onChange={(e) => setSearchData({...searchData, passengers: Number(e.target.value)})}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {[1, 2, 3, 4].map(num => (
                      <option key={num} value={num}>{num} passenger{num > 1 ? 's' : ''}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <Button
                onClick={handleSearch}
                disabled={!searchData.from || !searchData.to || !searchData.date}
                className="w-full bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600 py-3 text-lg"
              >
                Search Available Rides
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {step === 'results' && (
          <div className="p-6">
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
              <h3 className="font-semibold text-blue-900 mb-2">Search Details</h3>
              <p className="text-blue-700">
                {searchData.from} → {searchData.to} on {searchData.date} for {searchData.passengers} passenger{searchData.passengers > 1 ? 's' : ''}
              </p>
            </div>

            <div className="space-y-4">
              {sampleRides.map((ride) => (
                <div key={ride.id} className="bg-gray-50 rounded-xl p-6 hover:bg-gray-100 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-green-100 rounded-full flex items-center justify-center">
                        <span className="text-lg font-bold text-blue-600">
                          {ride.driver.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      
                      <div>
                        <div className="font-semibold text-gray-900">{ride.driver}</div>
                        <div className="text-sm text-gray-600">
                          ⭐ {ride.rating} • {ride.car} • {ride.seats} seats available
                        </div>
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-2xl font-bold text-blue-600">{ride.price}</div>
                      <div className="text-sm text-gray-600">{ride.time} • {ride.duration}</div>
                    </div>
                  </div>

                  <div className="mt-4 flex justify-end">
                    <Button
                      onClick={handleBookRide}
                      className="bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600"
                    >
                      Book This Ride
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-green-50 rounded-xl border border-blue-200">
              <h3 className="font-bold text-gray-900 mb-2">🚨 Sign In Required</h3>
              <p className="text-gray-700 mb-4">
                To book a ride and ensure safety for all travelers, you'll need to create an account or sign in. 
                This helps us verify your identity and maintain our community standards.
              </p>
              <Button
                onClick={onAuthRequired}
                className="bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600"
              >
                Continue with Sign In
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
